import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Lock } from 'lucide-react';
import {
    TRIAL_SCOPE_SENTENCE,
    accessRequestMailto,
    featureBlurb,
} from '../auth/features';

interface LockedFeatureDialogProps {
    /** Name shown to the user — the nav label they clicked. */
    name: string;
    /** Feature key, for the one-line description. */
    feature?: string;
    onClose: () => void;
}

/**
 * Small accessible dialog for a locked sidebar item: what the feature does
 * and a Contact us mailto. Escape or Close dismisses; the caller restores
 * focus to the item. Rendered in a portal so the sidebar's overflow and the
 * mobile drawer cannot clip it; Escape is captured so it does not also close
 * the drawer.
 */
export function LockedFeatureDialog({ name, feature, onClose }: LockedFeatureDialogProps) {
    const panelRef = useRef<HTMLDivElement>(null);
    const contactRef = useRef<HTMLAnchorElement>(null);
    const blurb = feature ? featureBlurb(feature) : null;

    useEffect(() => {
        contactRef.current?.focus();
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                e.preventDefault();
                onClose();
                return;
            }
            if (e.key === 'Tab' && panelRef.current) {
                const focusables = Array.from(
                    panelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'),
                );
                if (focusables.length === 0) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [onClose]);

    return createPortal(
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
            role="presentation"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="locked-feature-title"
                aria-describedby="locked-feature-desc"
                data-testid="locked-feature-dialog"
                className="w-full max-w-sm rounded-lg border border-border bg-card p-5 shadow-xl space-y-3"
            >
                <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <Lock className="w-3.5 h-3.5" aria-hidden="true" />
                    Not included in your trial
                </p>
                <h2 id="locked-feature-title" className="text-base font-semibold text-foreground">{name}</h2>
                <div id="locked-feature-desc" className="space-y-2 text-sm text-muted-foreground">
                    {blurb && <p>{blurb}</p>}
                    <p>{TRIAL_SCOPE_SENTENCE} Contact us for access to {name}.</p>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm border border-border rounded-md hover:bg-muted"
                    >
                        Close
                    </button>
                    <a
                        ref={contactRef}
                        href={accessRequestMailto(name)}
                        className="px-4 py-2 text-sm rounded-md font-medium bg-primary text-primary-foreground hover:opacity-90"
                    >
                        Contact us
                    </a>
                </div>
            </div>
        </div>,
        document.body,
    );
}
